import { useCallback, useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { Copy, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePOS } from "@/context/POSContext";
import * as tg from "@/lib/integrationsApi";
import * as admin from "@/lib/adminApi";
import TelegramPanel from "@/components/admin/TelegramPanel";

/**
 * Settings → Integrations → Telegram (owner PRD §11, Phase 1, 2026-10-31).
 * Overview · Bot · People · Groups · Delivery · Audit. What each role gets is set in Staff → Roles.
 * A person gets an item when their POS permission allows it AND their own switch, or
 * else their role's template, says on. Locked items say why.
 */
const SECTIONS = [
  ["overview", "Overview"], ["bot", "Bot"], ["people", "People"],
  ["groups", "Groups"], ["delivery", "Delivery"], ["audit", "Audit"],
];
const reason = err => err?.response?.data?.detail || err?.message || "no answer from the server";
const when = iso => (iso ? new Date(iso).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const STATUS_WORDS = { connected: "Connected", blocked: "Blocked the bot", inactive: "Account inactive" };

function Card({ title, children, testId }) {
  return (
    <section className="rounded-lg border bg-card p-base space-y-snug" data-testid={testId}>
      {title && <h3 className="text-body font-semibold">{title}</h3>}
      {children}
    </section>
  );
}

function Overview({ token, go }) {
  const [o, setO] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { tg.telegramOverview(token).then(setO).catch(e => setError(reason(e))); }, [token]);
  if (error) return <p className="text-meta text-danger" role="alert">Overview not available: {error}</p>;
  if (!o) return <p className="text-meta text-muted-foreground">Loading…</p>;
  const tiles = [
    ["Bot", o.token_set ? (o.bot_username ? `@${o.bot_username}` : "Connected") : "Not connected", "bot", !o.token_set],
    ["People connected", o.people.connected, "people"],
    ["Blocked the bot", o.people.blocked, "people", o.people.blocked > 0],
    ["Invitations waiting", o.pending_invitations, "people"],
    ["Messages waiting", o.queue.waiting, "delivery"],
    ["Failed / gave up", o.queue.failed + o.queue.dead, "delivery", o.queue.failed + o.queue.dead > 0],
  ];
  return (
    <div className="space-y-base" data-testid="telegram-overview">
      {o.bot_poll_error && <p className="text-meta text-danger" role="alert">The bot is not reading messages: {o.bot_poll_error}</p>}
      {o.token_key_problem && <p className="text-meta text-danger" role="alert" data-testid="telegram-key-problem">The bot token cannot be used: {o.token_key_problem}.</p>}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-snug">
        {tiles.map(([label, value, target, bad]) => (
          <button key={label} type="button" onClick={() => go(target)}
                  className="text-left rounded-lg border bg-card p-base hover:bg-muted">
            <div className="text-meta text-muted-foreground">{label}</div>
            <div className={`text-lede font-semibold tabular-nums ${bad ? "text-danger" : ""}`}>{value}</div>
          </button>
        ))}
      </div>
      <p className="text-meta text-muted-foreground">Last message delivered: {when(o.last_sent_at)}</p>
    </div>
  );
}

function PersonDrawer({ person, token, onChanged }) {
  const [busy, setBusy] = useState(false);
  const groups = useMemo(() => {
    const g = {};
    for (const s of person.switches) (g[`${s.kind === "notification" ? "Messages · " : "In the chat · "}${s.group}`] ||= []).push(s);
    return g;
  }, [person]);
  const change = async (key, effect) => {
    setBusy(true);
    try { onChanged(await tg.setTelegramSwitches(person.link_id, { [key]: effect }, token)); }
    catch (e) { toast.error(`Not changed: ${reason(e)}`); }
    finally { setBusy(false); }
  };
  const reset = async () => {
    setBusy(true);
    try { onChanged(await tg.resetTelegramSwitches(person.link_id, token)); toast.success("Back to the role's defaults"); }
    catch (e) { toast.error(`Not reset: ${reason(e)}`); }
    finally { setBusy(false); }
  };
  return (
    <div className="border-t pt-snug space-y-snug" data-testid={`telegram-person-${person.link_id}-switches`}>
      {Object.entries(groups).map(([title, items]) => (
        <div key={title}>
          <div className="text-meta font-medium text-muted-foreground">{title}</div>
          {items.map(s => (
            <div key={s.key} className="flex items-center gap-snug py-hair">
              <span className="flex-1 text-body">{s.label}</span>
              {s.source === "locked" ? (
                <span className="text-micro text-muted-foreground max-w-[18rem] text-right">🔒 {s.reason}</span>
              ) : (
                <>
                  <span className="text-micro text-muted-foreground">
                    {s.source === "person" ? "set for them" : `from role (${s.role_default ? "on" : "off"})`}
                  </span>
                  <select className="h-9 rounded-md border bg-background px-snug text-meta" disabled={busy}
                          value={s.source === "person" ? (s.on ? "allow" : "deny") : "inherit"}
                          onChange={e => change(s.key, e.target.value)} data-testid={`telegram-switch-${person.link_id}-${s.key}`}>
                    <option value="inherit">Role default</option>
                    <option value="allow">On</option>
                    <option value="deny">Off</option>
                  </select>
                </>
              )}
            </div>
          ))}
        </div>
      ))}
      <Button variant="outline" size="sm" disabled={busy} onClick={reset} data-testid={`telegram-reset-${person.link_id}`}>
        Reset to role defaults
      </Button>
    </div>
  );
}

function People({ token }) {
  const [rows, setRows] = useState(null);
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    Promise.all([tg.telegramPeople(token), tg.telegramInvitations(token)])
      .then(([p, i]) => { setRows(p); setInvites(i); setError(null); })
      .catch(e => setError(reason(e)));
  }, [token]);
  useEffect(() => { load(); }, [load]);
  // While an invitation waits, look again every few seconds: the row appears by
  // itself when they tap Start (PRD §4.1 "automatically updates the connection status").
  useEffect(() => {
    if (!invites.length) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [invites.length, load]);
  const revoke = async id => {
    try { await tg.revokeTelegramInvitation(id, token); toast.success("Invitation cancelled"); load(); }
    catch (e) { toast.error(`Not cancelled: ${reason(e)}`); }
  };
  const unlink = async p => {
    try { await tg.removeLink(p.link_id, token); toast.success(`${p.name} disconnected`); load(); }
    catch (e) { toast.error(`Not disconnected: ${reason(e)}`); }
  };
  if (error) return <p className="text-meta text-danger" role="alert">People not loaded: {error}</p>;
  if (!rows) return <p className="text-meta text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-base" data-testid="telegram-people">
      <AddPerson token={token} connected={rows} onIssued={load} />
      {invites.length > 0 && (
        <Card title="Invitations waiting">
          {invites.map(i => (
            <div key={i.id} className="flex items-center gap-snug text-body">
              <span className="flex-1">{i.name} · {i.kind === "self" ? "linking themselves" : "invited"} · until {when(i.expires_at)}</span>
              <Button variant="ghost" size="sm" onClick={() => revoke(i.id)} data-testid={`telegram-revoke-${i.id}`}>Cancel</Button>
            </div>
          ))}
        </Card>
      )}
      <Card title={`Connected people (${rows.length})`}>
        {rows.length === 0 && <p className="text-meta text-muted-foreground">Nobody yet. Connect someone above.</p>}
        {rows.map(p => {
          const on = p.switches.filter(s => s.on);
          return (
            <div key={p.link_id} className="border-t first:border-t-0 py-snug" data-testid={`telegram-person-${p.link_id}`}>
              <div className="flex flex-wrap items-center gap-snug">
                <button type="button" className="flex-1 min-w-0 text-left" onClick={() => setOpen(open === p.link_id ? null : p.link_id)}>
                  <span className="text-body font-semibold">{p.name}</span>
                  <span className="text-meta text-muted-foreground"> · {p.role}{p.telegram_name ? ` · Telegram: ${p.telegram_name}` : ""}</span>
                  <span className="block text-meta text-muted-foreground">
                    {on.filter(s => s.kind === "capability").length} in the chat · {on.filter(s => s.kind === "notification").length} messages
                    {p.last_delivered_at ? ` · last delivered ${when(p.last_delivered_at)}` : ""}
                  </span>
                </button>
                <span className={`text-meta font-medium ${p.status === "connected" ? "text-success" : "text-danger"}`}>{STATUS_WORDS[p.status] || p.status}</span>
                <Button variant="outline" size="sm" onClick={() => setOpen(open === p.link_id ? null : p.link_id)}>
                  {open === p.link_id ? "Close" : "What they get"}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => unlink(p)}>Disconnect</Button>
              </div>
              {p.status === "blocked" && <p className="text-meta text-danger">They blocked the bot — ask them to open it and tap Start.</p>}
              {p.last_error && p.status !== "blocked" && <p className="text-meta text-danger">Last failure: {p.last_error}</p>}
              {open === p.link_id && <PersonDrawer person={p} token={token} onChanged={u => setRows(rs => rs.map(r => (r.link_id === u.link_id ? u : r)))} />}
            </div>
          );
        })}
      </Card>
    </div>
  );
}

/**
 * People -> Connect a person (design section 3, "Add person -> invitation"). Replaces
 * the step-1 linking card, which showed a second list of the same people (owner,
 * 2026-11-01: "2 separate chat link sections"). One-time link: QR to scan, or
 * Copy / WhatsApp when the person is not at the counter.
 */
function AddPerson({ token, connected, onIssued }) {
  const [users, setUsers] = useState([]);
  const [personId, setPersonId] = useState("");
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    admin.listUsers(token)
      .then(u => setUsers((u || []).filter(x => x.is_active)))
      .catch(e => setError(reason(e)));
  }, [token]);
  const linkedIds = new Set((connected || []).map(p => p.user_id));
  const choices = users.filter(u => !linkedIds.has(u.id));
  const issue = async () => {
    setBusy(true);
    try {
      const res = await tg.createLinkCode(Number(personId), token);
      const qr = await QRCode.toDataURL(res.link, { margin: 1, width: 240 });
      setIssued({ ...res, qr });
      onIssued?.();
    } catch (err) {
      toast.error(`Invitation not made: ${reason(err)}`);
    } finally {
      setBusy(false);
    }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(issued.link);
      toast.success("Link copied — send it to them; it works once");
    } catch {
      document.getElementById("telegram-link-text")?.select();
      toast.error("Could not copy — the link is selected below, copy it by hand");
    }
  };
  const whatsappUrl = issued
    ? `https://wa.me/?text=${encodeURIComponent(`Tap to connect Vibez on Telegram (works once, for 10 minutes): ${issued.link}`)}`
    : "";
  return (
    <Card title="Connect a person" testId="telegram-add-person">
      <p className="text-meta text-muted-foreground">
        They scan the code (or open the link you send) and tap Start. What the bot then shows them follows their role and
        their own switches below.
      </p>
      {error && <p className="text-meta text-danger" role="alert">Staff list not loaded: {error}</p>}
      <div className="flex flex-wrap gap-snug">
        <Select value={personId} onValueChange={v => { setPersonId(v); setIssued(null); }}>
          <SelectTrigger className="w-full sm:w-64" data-testid="telegram-link-person"><SelectValue placeholder="Choose a person" /></SelectTrigger>
          <SelectContent>
            {choices.map(u => <SelectItem key={u.id} value={String(u.id)}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="action" disabled={busy || !personId} onClick={issue} data-testid="telegram-link-issue">Make invitation</Button>
      </div>
      {issued && (
        <div className="flex flex-wrap items-start gap-base pt-snug" data-testid="telegram-link-issued">
          <img src={issued.qr} alt={`Telegram link for ${issued.user_name}`} className="w-48 h-48 rounded border bg-white" />
          <div className="text-meta space-y-1 min-w-0 flex-1">
            <div className="text-body font-medium">{issued.user_name}: scan with your phone camera, then tap Start</div>
            <div className="text-muted-foreground">Works once, until {when(issued.expires_at)}.</div>
            <div className="flex flex-wrap gap-snug pt-1">
              <Button variant="outline" size="sm" onClick={copyLink} data-testid="telegram-link-copy"><Copy className="w-4 h-4" /> Copy link</Button>
              <Button variant="outline" size="sm" asChild>
                <a href={whatsappUrl} target="_blank" rel="noreferrer" data-testid="telegram-link-whatsapp"><Share2 className="w-4 h-4" /> Send on WhatsApp</a>
              </Button>
            </div>
            <input id="telegram-link-text" readOnly value={issued.link} className="w-full text-micro bg-muted rounded px-1 py-0.5" />
          </div>
        </div>
      )}
    </Card>
  );
}

function Delivery({ token }) {
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState(null);
  const load = useCallback(() => { tg.telegramDelivery(status, token).then(setRows).catch(e => toast.error(reason(e))); }, [status, token]);
  useEffect(() => { load(); }, [load]);
  const retry = async id => {
    try { await tg.retryTelegramMessage(id, token); toast.success("Queued to send again"); load(); }
    catch (e) { toast.error(`Not queued: ${reason(e)}`); }
  };
  return (
    <Card title="Messages" testId="telegram-delivery">
      <select className="h-9 rounded-md border bg-background px-snug text-meta" value={status} onChange={e => setStatus(e.target.value)}>
        <option value="">All</option><option value="queued">Waiting</option><option value="sent">Sent</option>
        <option value="failed">Failed</option><option value="dead">Gave up</option>
      </select>
      {!rows ? <p className="text-meta text-muted-foreground">Loading…</p> : rows.length === 0 ? (
        <p className="text-meta text-muted-foreground">No messages.</p>
      ) : rows.map(r => (
        <div key={r.id} className="flex flex-wrap items-center gap-snug border-t py-tight text-body">
          <span className="flex-1 min-w-0">{r.kind.replaceAll("_", " ")} → {r.to || r.chat_id} <span className="text-meta text-muted-foreground">{when(r.sent_at || r.created_at)}</span></span>
          <span className={`text-meta ${r.status === "sent" ? "text-success" : r.status === "queued" ? "text-muted-foreground" : "text-danger"}`}>
            {({ queued: "Waiting", sent: "Sent", failed: "Failed", dead: "Gave up" })[r.status]}{r.attempts > 1 ? ` · ${r.attempts} tries` : ""}
          </span>
          {r.last_error && r.status !== "sent" && <span className="basis-full text-meta text-danger">{r.last_error}</span>}
          {(r.status === "failed" || r.status === "dead") && <Button variant="outline" size="sm" onClick={() => retry(r.id)}>Retry</Button>}
        </div>
      ))}
    </Card>
  );
}

function Audit({ token }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { tg.telegramAudit(token).then(setRows).catch(e => toast.error(reason(e))); }, [token]);
  const words = a => a.replace("integrations.telegram_", "").replaceAll("_", " ");
  return (
    <Card title="Who did what" testId="telegram-audit">
      {!rows ? <p className="text-meta text-muted-foreground">Loading…</p> : rows.length === 0 ? (
        <p className="text-meta text-muted-foreground">Nothing yet.</p>
      ) : rows.map(r => (
        <div key={r.id} className="flex gap-snug border-t py-tight text-body">
          <span className="text-meta text-muted-foreground tabular-nums w-28 shrink-0">{when(r.at)}</span>
          <span className="flex-1">{r.who || "Telegram"} · {words(r.action)}</span>
        </div>
      ))}
    </Card>
  );
}

export default function TelegramHub() {
  const { token } = usePOS();
  const [section, setSection] = useState("overview");
  return (
    <div className="space-y-base rounded-lg border bg-card p-base" data-testid="telegram-hub">
      <div>
        <h2 className="text-lede font-semibold">Telegram</h2>
        <p className="text-meta text-muted-foreground">
          Staff and partners use Vibez from Telegram — sales, KOTs, alerts — each within what their role allows.
        </p>
      </div>
      <div className="flex gap-snug overflow-x-auto border-b">
        {SECTIONS.map(([id, label]) => (
          <button key={id} type="button" onClick={() => setSection(id)} data-testid={`telegram-section-${id}`}
                  className={`px-snug pb-snug text-body whitespace-nowrap border-b-2 ${section === id ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground"}`}>
            {label}
          </button>
        ))}
      </div>
      {section === "overview" && <Overview token={token} go={setSection} />}
      {section === "bot" && <TelegramPanel part="bot" />}
      {section === "people" && <People token={token} />}
      {section === "groups" && <TelegramPanel part="groups" />}
      {section === "delivery" && <Delivery token={token} />}
      {section === "audit" && <Audit token={token} />}
    </div>
  );
}
